import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

import { isCapacitor, isDesktopApp } from '../../utils/appUtils';

// DO NOT TOUCH THIS! USE `.env` TO CHANGE VARIABLS VALUE
// Initialize Firebase
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_firebaseApiKey,
  authDomain: import.meta.env.VITE_firebaseAuthDomain,
  databaseURL: import.meta.env.VITE_firebaseDatabaseURL,
  projectId: import.meta.env.VITE_firebaseProjectId,
  storageBucket: import.meta.env.VITE_firebaseStorageBucket,
  messagingSenderId: import.meta.env.VITE_firebaseMessagingSenderId,
  appId: import.meta.env.VITE_firebaseAppId,
  measurementId: import.meta.env.VITE_firebaseMeasurementId
};

const config = firebaseConfig;

if (import.meta.env.DEV) {
  console.log('firebase config ');
}

// Initialize Firebase with error handling - use dynamic imports to avoid IndexedDB issues
let firebaseApp, firebaseDB, firebaseAuth, firebaseStorage, fireAnalytics, fireMessaging;

// Initialize Firebase core services dynamically to avoid IndexedDB issues
const initializeFirebase = async () => {
  try {
    const { initializeApp } = await import('firebase/app');
    firebaseApp = initializeApp(config);
    
    const { getFirestore } = await import('firebase/firestore');
    firebaseDB = getFirestore(firebaseApp);
    
    const { getAuth } = await import('firebase/auth');
    firebaseAuth = getAuth(firebaseApp);
    
    const { getStorage } = await import('firebase/storage');
    firebaseStorage = getStorage(firebaseApp);
    
    // Skip analytics and messaging to avoid IndexedDB issues
    console.warn('Skipping Firebase Analytics and Messaging to avoid IndexedDB issues');
    fireAnalytics = null;
    fireMessaging = null;
    
    return true;
  } catch (error) {
    console.error('Firebase initialization failed:', error);
    // Create fallback objects to prevent app crashes
    firebaseApp = null;
    firebaseDB = null;
    firebaseAuth = null;
    firebaseStorage = null;
    fireAnalytics = null;
    fireMessaging = null;
    return false;
  }
};

// Initialize Firebase immediately
initializeFirebase().catch(error => {
  console.error('Firebase initialization failed:', error);
});

// Set firebaseAuth on window for development after initialization
setTimeout(() => {
  if (import.meta.env.MODE === 'development' && firebaseAuth) {
    window.firebaseAuth = firebaseAuth;
  }
}, 100);

const requestWebNotificationPermission = async () => {
  try {
    if (!fireMessaging) {
      console.warn('Firebase Messaging not available (IndexedDB issue)');
      return;
    }
    
    const permisssion = await Notification.requestPermission();
    if (permisssion !== 'granted') {
      throw new Error('permission-denied');
    }
    
    const { getToken } = await import('firebase/messaging');
    const token = await getToken(fireMessaging, { vapidKey: import.meta.env.VITE_vapidKey });
    if (token) {
      console.log('FCMToken: token', token);
      const currentUser = firebaseAuth.currentUser;
      if (currentUser && firebaseDB) {
        const { doc, setDoc } = await import('firebase/firestore');
        const userRef = doc(firebaseDB, 'glo-users', currentUser.uid);
        await setDoc(userRef, { webToken: token }, { merge: true });
      }
      return token;
    }
  } catch (error) {
    console.error('Permission denied or error occurred:', error);
  }
};

const subscribeForegroundNotification = () => (_dispatch) => {
  if (!fireMessaging) {
    console.warn('Firebase Messaging not available (IndexedDB issue)');
    return;
  }
  
  try {
    const { onMessage } = require('firebase/messaging');
    onMessage(fireMessaging, (payload) => {
      console.log('FCMToken: Message received. ', payload);
    });
  } catch (error) {
    console.warn('Failed to subscribe to foreground notifications:', error);
  }
};

const logoutEveryWhere = async () => {
  if (isCapacitor) {
    const result = await FirebaseAuthentication.getCurrentUser();
    if (result?.user) {
      await FirebaseAuthentication.signOut();
    }
    return;
  }

  if (firebaseAuth) {
    try {
      const { signOut } = await import('firebase/auth');
      const currentUser = firebaseAuth.currentUser;
      if (currentUser) {
        await signOut(firebaseAuth);
      }
    } catch (error) {
      console.warn('Firebase signOut failed:', error);
    }
  }
};

const getUserToken = async () => {
  try {
    if (!firebaseAuth) {
      console.warn('Firebase Auth not available');
      return '';
    }
    
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      throw new Error('user-not-found');
    }
    
    const { getIdToken } = await import('firebase/auth');
    const token = await getIdToken(currentUser);
    return token;
  } catch (error) {
    console.log('token error', error);
    return '';
  }
};

const recordAnalytics = (eventName) => {
  if (isCapacitor) {
    try {
      FirebaseAnalytics.logEvent({ name: eventName });
    } catch (error) {
      console.warn('Capacitor analytics failed:', error);
    }
    return;
  }
  
  if (!fireAnalytics) {
    console.warn('Firebase Analytics not available (IndexedDB issue)');
    return;
  }
  
  try {
    const { logEvent } = require('firebase/analytics');
    if (window.isNewUser) {
      logEvent(fireAnalytics, `new_${eventName}`);
    } else if (window.withNotification) {
      logEvent(fireAnalytics, `noti_${eventName}`);
    } else {
      logEvent(fireAnalytics, eventName);
    }
  } catch (error) {
    console.warn('Analytics logging failed:', error);
  }
};

async function logoutWithCleanup(reason?: string) {
  if (!reason) {
    recordAnalytics('userDidLogout');
  }
  await logoutEveryWhere();
  localStorage.clear();
  sessionStorage.clear();
  if (isDesktopApp) {
    await window.CapacitorCustomPlatform.logoutUser();
    return;
  }
  // todo: use react router, sometimes extension will crash, cause x-frame sameorigin issue.
  window.location.reload();
}

export {
  requestWebNotificationPermission,
  subscribeForegroundNotification,
  fireMessaging,
  firebaseAuth,
  firebaseDB,
  logoutEveryWhere,
  getUserToken,
  firebaseStorage,
  recordAnalytics,
  logoutWithCleanup
};
